import { z } from 'zod';
import { enumFilterSchema } from 'v5/api/_common/query/filters/EnumFilter';
import { stringFilterSchema } from 'v5/api/_common/query/filters/StringFilter';
import { booleanFilterSchema } from 'v5/api/_common/query/filters/BooleanFilter';
import { dateFilterSchemaOf } from 'v5/api/_common/query/filters/GenericDateFilter';
import { numberFilterSchema } from 'v5/api/_common/query/filters/NumberFilter';
import { CORE_FIELD_NAMES } from 'v5/features/list-filters/metastructure-filters/core/fields';
import { timestampFilter } from '../standard-date-filter/timestamp-filter';

export const strToNull = (str: unknown) => (str === '' ? null : str);

/**
 * Unified Zod schema for all core field filters
 * All fields are optional since different entities use different subsets
 */
export const coreFieldFiltersSchema = z
  .object({
    // Production fields
    [CORE_FIELD_NAMES.STATUS]: z.preprocess(
      strToNull,
      enumFilterSchema(z.coerce.number()).nullable()
    ),
    [CORE_FIELD_NAMES.PRODUCTION_DATE]: z.preprocess(
      strToNull,
      dateFilterSchemaOf([
        'today',
        'tomorrow',
        'nextWeek',
        'nextMonth'
      ]).nullable()
    ),
    [CORE_FIELD_NAMES.CODE]: z.preprocess(
      strToNull,
      stringFilterSchema.nullable()
    ),
    [CORE_FIELD_NAMES.ARCHIVED]: z.preprocess(
      strToNull,
      booleanFilterSchema.nullable()
    ),
    [CORE_FIELD_NAMES.USER_ID]: z.preprocess(
      strToNull,
      enumFilterSchema(z.string()).nullable()
    ),
    [CORE_FIELD_NAMES.PRODUCTION_TYPE_ID]: z.preprocess(
      strToNull,
      enumFilterSchema(z.coerce.number()).nullable()
    ),
    [CORE_FIELD_NAMES.ICON]: z.preprocess(
      strToNull,
      stringFilterSchema.nullable()
    ),
    [CORE_FIELD_NAMES.COLOR]: z.preprocess(
      strToNull,
      stringFilterSchema.nullable()
    ),
    [CORE_FIELD_NAMES.TITLE]: z.preprocess(
      strToNull,
      stringFilterSchema.nullable()
    ),
    [CORE_FIELD_NAMES.DESCRIPTION]: z.preprocess(
      strToNull,
      stringFilterSchema.nullable()
    ),
    [CORE_FIELD_NAMES.BUDGET_AMOUNT]: z.preprocess(
      strToNull,
      numberFilterSchema.nullable()
    ),

    // Resource fields
    [CORE_FIELD_NAMES.RESOURCE_TYPE_ID]: z.preprocess(
      strToNull,
      enumFilterSchema(z.coerce.number()).nullable()
    ),
    [CORE_FIELD_NAMES.ORGANISATION_ID]: z.preprocess(
      strToNull,
      enumFilterSchema(z.coerce.number()).nullable()
    ),
    [CORE_FIELD_NAMES.REPRESENTED_BY_ID]: z.preprocess(
      strToNull,
      enumFilterSchema(z.coerce.number()).nullable()
    ),
    [CORE_FIELD_NAMES.ROLE]: z.preprocess(
      strToNull,
      stringFilterSchema.nullable()
    ),
    [CORE_FIELD_NAMES.TAGS]: z.preprocess(
      strToNull,
      enumFilterSchema(z.string()).nullable()
    ),
    [CORE_FIELD_NAMES.GROUPS]: z.preprocess(
      strToNull,
      enumFilterSchema(z.coerce.number()).nullable()
    ),
    [CORE_FIELD_NAMES.EMAILS]: z.preprocess(
      strToNull,
      stringFilterSchema.nullable()
    ),
    [CORE_FIELD_NAMES.PHONES]: z.preprocess(
      strToNull,
      stringFilterSchema.nullable()
    ),
    [CORE_FIELD_NAMES.WEBSITES]: z.preprocess(
      strToNull,
      stringFilterSchema.nullable()
    ),
    [CORE_FIELD_NAMES.SOCIALS]: z.preprocess(
      strToNull,
      stringFilterSchema.nullable()
    ),
    [CORE_FIELD_NAMES.LOCATIONS]: z.preprocess(
      strToNull,
      stringFilterSchema.nullable()
    ),
    // Common fields
    [CORE_FIELD_NAMES.CREATED_AT]: z.preprocess(
      strToNull,
      timestampFilter.schema.nullable()
    ),
    [CORE_FIELD_NAMES.UPDATED_AT]: z.preprocess(
      strToNull,
      timestampFilter.schema.nullable()
    )
  })
  .partial();
